import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { KleurenOrder } from "@/hooks/useKleurenOrders";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

interface KleurenOrdersTableProps {
  orders: KleurenOrder[];
  isLoading: boolean;
  selectedOrders: Set<string>;
  onToggleSelect: (ordernumber: string) => void;
  onSelectAll: (selected: boolean) => void;
  onOrderClick: (ordernumber: string) => void;
  status?: string;
  onStatusChange?: (status: string) => void;
}

const KleurenOrdersTable = ({ orders, isLoading, selectedOrders, onToggleSelect, onSelectAll, onOrderClick, status, onStatusChange }: KleurenOrdersTableProps) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className="text-center py-12 space-y-3">
        <p className="text-muted-foreground">
          {status === 'open' ? 'Geen open kleuren orders gevonden' : 'Geen kleuren orders gevonden'}
        </p>
        {status === 'open' && onStatusChange && (
          <button
            onClick={() => onStatusChange('all')}
            className="text-sm text-primary hover:underline font-medium"
          >
            Bekijk alle statussen
          </button>
        )}
      </div>
    );
  }

  const allSelected = orders.length > 0 && orders.every(o => selectedOrders.has(o.ordernumber));

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="hidden sm:grid grid-cols-[40px_1fr_1fr_1fr_120px] gap-2 px-4 py-2 bg-muted/50 text-sm font-medium text-muted-foreground">
        <div>
          <Checkbox checked={allSelected} onCheckedChange={(checked) => onSelectAll(!!checked)} />
        </div>
        <div>Order / Kleur</div>
        <div>Product</div>
        <div>Merk</div>
        <div className="text-right">Datum</div>
      </div>

      {/* Rows */}
      {orders.map((order) => {
        const dateStr = order.createdat ? format(new Date(order.createdat), 'dd MMM HH:mm', { locale: nl }) : '-';
        return (
          <div
            key={`${order.ordernumber}-${order.sku}`}
            className="grid grid-cols-[40px_1fr] sm:grid-cols-[40px_1fr_1fr_1fr_120px] gap-2 px-4 py-3 border-t hover:bg-muted/30 cursor-pointer items-center"
            onClick={() => onOrderClick(order.ordernumber)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selectedOrders.has(order.ordernumber)}
                onCheckedChange={() => onToggleSelect(order.ordernumber)}
              />
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{order.ordernumber}</div>
              <div className="text-xs text-primary font-semibold truncate">{order.customfieldcolor || '-'}</div>
              {/* Mobile-only extra info */}
              <div className="sm:hidden text-xs text-muted-foreground truncate mt-0.5">
                {order.producttitle} · {order.brand_title || '-'} · {dateStr}
              </div>
            </div>
            <div className="hidden sm:block text-sm truncate">{order.producttitle}</div>
            <div className="hidden sm:block text-sm text-muted-foreground truncate">{order.brand_title || '-'}</div>
            <div className="hidden sm:block text-sm text-muted-foreground text-right">{dateStr}</div>
          </div>
        );
      })}
    </div>
  );
};

export default KleurenOrdersTable;
